import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useQueryKeys } from '@/shared/hooks/use-query-keys';
import { UpdateSavedListOutreachStatus } from '../api';
import type {
  SavedListOutreachStatusItemDTO,
  UpdateSavedListOutreachStatusRequestDTO,
} from '../dto';

type UpdateSavedListOutreachStatusResult = SavedListOutreachStatusItemDTO & {
  locally_saved?: boolean;
};

export function useUpdateSavedListOutreachStatus() {
  const queryClient = useQueryClient();
  const queryKeys = useQueryKeys();

  return useMutation({
    mutationFn: async ({
      listId,
      request,
    }: {
      listId: string;
      request: UpdateSavedListOutreachStatusRequestDTO;
    }): Promise<UpdateSavedListOutreachStatusResult> => {
      try {
        return await UpdateSavedListOutreachStatus(listId, request);
      } catch {
        // Mock/demo workspaces may not have a persisted outreach-status endpoint yet.
        // Keep the manual status useful by treating the optimistic cache update as local state.
        return {
          creator_id: request.creator_id,
          channel: request.channel,
          status: request.status,
          updated_at: new Date().toISOString(),
          locally_saved: true,
        };
      }
    },
    onMutate: async ({ listId, request }) => {
      const queryKey = queryKeys.savedCreatorsList.outreachStatuses(listId);
      await queryClient.cancelQueries({ queryKey });
      const previousStatuses =
        queryClient.getQueryData<SavedListOutreachStatusItemDTO[]>(queryKey) ?? [];

      queryClient.setQueryData<SavedListOutreachStatusItemDTO[]>(queryKey, (current = []) => {
        const nextStatus = {
          creator_id: request.creator_id,
          channel: request.channel,
          status: request.status,
        };
        const existingIndex = current.findIndex(
          (item) => item.creator_id === request.creator_id && item.channel === request.channel,
        );

        if (existingIndex === -1) {
          return [...current, nextStatus];
        }

        return current.map((item, index) =>
          index === existingIndex ? { ...item, ...nextStatus } : item,
        );
      });

      return { previousStatuses, queryKey };
    },
    onSuccess: (data, { listId }) => {
      const queryKey = queryKeys.savedCreatorsList.outreachStatuses(listId);
      queryClient.setQueryData<SavedListOutreachStatusItemDTO[]>(queryKey, (current = []) => {
        const nextStatus = {
          creator_id: data.creator_id,
          channel: data.channel,
          status: data.status,
          updated_at: data.updated_at,
        };
        const existingIndex = current.findIndex(
          (item) => item.creator_id === data.creator_id && item.channel === data.channel,
        );

        if (existingIndex === -1) {
          return [...current, nextStatus];
        }

        return current.map((item, index) =>
          index === existingIndex ? { ...item, ...nextStatus } : item,
        );
      });

      if (data.locally_saved) return;

      queryClient.invalidateQueries({
        queryKey: queryKeys.savedCreatorsList.outreachStatuses(listId),
      });
    },
  });
}
